import type { InactiveTimeUI } from '@/reducers/thermostatInactivityPeriods'
import type { ThermostatComfortSetting } from '@/types/thermostatComfortSetting'
import type { InactiveComfortSetting } from '@/reducers/inactiveComfortSettings'

export const convert24To12Hour = (
  time24: string,
  addNextDayIndicator = false,
): string => {
  const [hours, minutes] = time24.split(':').map(Number)
  const period = hours >= 12 ? 'PM' : 'AM'
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
  const timeString = `${hours12}:${minutes
    .toString()
    .padStart(2, '0')} ${period}`
  return addNextDayIndicator ? `${timeString}⁺¹` : timeString
}

export function getInactivePeriodSummaryLines(
  localInactiveTimes: InactiveTimeUI[],
): string[] {
  const daysOfWeek = [
    { short: 'Sun', full: 'Sunday' },
    { short: 'Mon', full: 'Monday' },
    { short: 'Tue', full: 'Tuesday' },
    { short: 'Wed', full: 'Wednesday' },
    { short: 'Thu', full: 'Thursday' },
    { short: 'Fri', full: 'Friday' },
    { short: 'Sat', full: 'Saturday' },
  ]

  const crossesMidnight = (startTime: string, endTime: string): boolean => {
    const [startHour, startMinute] = startTime.split(':').map(Number)
    const [endHour, endMinute] = endTime.split(':').map(Number)
    const startMinutes = startHour * 60 + startMinute
    const endMinutes = endHour * 60 + endMinute
    return endMinutes < startMinutes
  }

  const formatTimeRange = (startTime: string, endTime: string): string => {
    const crossesOverMidnight = crossesMidnight(startTime, endTime)
    const startTimeStr = convert24To12Hour(startTime)
    const endTimeStr = convert24To12Hour(endTime, crossesOverMidnight)
    return `${startTimeStr} - ${endTimeStr}`
  }

  const getDayGroupName = (days: number[]): string => {
    if (days.length === 0) return ''

    const sortedDays = [...days].sort((a, b) => a - b)

    const hasWeekends = days.includes(0) && days.includes(6)
    const weekendDays = days.filter((day) => day === 0 || day === 6)

    const weekdayIndices = [1, 2, 3, 4, 5]
    const hasAllWeekdays = weekdayIndices.every((day) => days.includes(day))

    if (hasWeekends && weekendDays.length === 2 && days.length === 2) {
      return 'On Weekends'
    }

    if (hasAllWeekdays && days.length === 5) {
      return 'On Weekdays'
    }

    if (days.length === 7) {
      return 'Every day'
    }

    return sortedDays.map((dayIndex) => daysOfWeek[dayIndex].short).join(', ')
  }

  const timeGroups = new Map<string, number[]>()

  const validSchedules = localInactiveTimes.filter(
    (schedule) => schedule.days.length > 0,
  )

  for (const schedule of validSchedules) {
    const timeRange = formatTimeRange(schedule.startTime, schedule.endTime)

    if (!timeGroups.has(timeRange)) {
      timeGroups.set(timeRange, [])
    }

    const existingDays = timeGroups.get(timeRange)
    if (existingDays) {
      for (const day of schedule.days) {
        if (!existingDays.includes(day)) {
          existingDays.push(day)
        }
      }
    }
  }

  const summaryLines: string[] = []

  for (const [timeRange, days] of timeGroups) {
    const dayGroupName = getDayGroupName(days)
    if (dayGroupName) {
      summaryLines.push(`${dayGroupName}: ${timeRange}`)
    }
  }

  return summaryLines.sort()
}

export function formatInlineList(names: string[]): string {
  const unique = [...new Set(names.filter(Boolean))]
  if (unique.length === 0) return ''
  if (unique.length === 1) return unique[0]
  if (unique.length === 2) return `${unique[0]} & ${unique[1]}`
  const head = unique.slice(0, -1).join(', ')
  const tail = unique[unique.length - 1]
  return `${head}, & ${tail}`
}

export function getSelectedScheduleNamesSummary(
  comfortSettings: ThermostatComfortSetting[],
  inactiveSettings: InactiveComfortSetting[],
): string {
  const selectedIds = new Set<number>()
  for (const item of inactiveSettings) {
    const csId = item?.comfort_setting?.id
    if (typeof csId === 'number') selectedIds.add(csId)
  }
  const names = comfortSettings
    .filter((cs) => selectedIds.has(cs.id))
    .map((cs) => cs.name)
    .filter(Boolean)
  return formatInlineList(names)
}
